import { useSyncExternalStore } from "react";

/**
 * A form somebody started and has not finished (SPEC §4.11).
 *
 * The sheets in this app are modal, which is right while somebody is filling
 * one in and wrong the moment they need to check something else — «а мы точно
 * не заняты в среду?» is a question you answer by leaving the form, and until
 * now leaving it meant losing it. So a sheet can be pushed down instead of
 * closed: it becomes a line above the tab bar, the app is free again, and the
 * form comes back with everything in it.
 *
 * **The draft lives here and not in the screen that opened it.** That is the
 * whole point: the screen unmounts the moment somebody switches tabs, and a
 * draft that lived in its state would go with it. What survives is the values,
 * plus two closures the screen supplied — «is this enough to save» and «save
 * it» — which is what lets the collapsed line finish the job without the
 * screen it came from ever being mounted again.
 *
 * One at a time, deliberately. Two half-written things and a bar that has to
 * choose between them is a worse problem than «finish this one first», and
 * for two people writing a shopping list it is not a problem at all.
 */

export interface Draft<V = unknown> {
  /** Which form this is — `place.new`, `event.new`. One per kind. */
  key: string;
  /** The tab it belongs to, so the collapsed line can take you back to it. */
  path: string;
  /** What the sheet's header said. */
  title: string;
  values: V;
  /** Enough to save, as the screen that opened it judges. */
  isReady(values: V): boolean;
  submit(values: V): void;
  /** Pushed down out of the way, but not gone. */
  minimised: boolean;
}

let held: Draft<never> | null = null;
const listeners = new Set<() => void>();

function announce(next: Draft<never> | null): void {
  // A new object every time, never a mutation: `useSyncExternalStore` compares
  // snapshots by identity and would show nothing at all for an edit in place.
  held = next;
  for (const listener of listeners) listener();
}

export function openDraft<V>(spec: Omit<Draft<V>, "minimised">): void {
  announce({ ...spec, minimised: false } as unknown as Draft<never>);
}

/** Every keystroke. Cheap: one object, one set of listeners, no rendering above. */
export function patchDraft<V>(values: V): void {
  if (!held) return;
  announce({ ...held, values } as unknown as Draft<never>);
}

export function minimiseDraft(): void {
  if (!held || held.minimised) return;
  announce({ ...held, minimised: true });
}

export function expandDraft(): void {
  if (!held || !held.minimised) return;
  announce({ ...held, minimised: false });
}

export function closeDraft(): void {
  announce(null);
}

/**
 * Save it and let it go.
 *
 * Cleared before the write rather than after: the write goes through the
 * outbox and draws immediately, so a line still saying «черновик» over the
 * thing it just created is a line about nothing.
 */
export function commitDraft(): void {
  const draft = held;
  if (!draft || !draft.isReady(draft.values)) return;

  announce(null);
  draft.submit(draft.values);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => held;

/** The draft, whatever it is, or null. */
export function useDraft(): Draft<never> | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * The values of *this* form, while it is the open one.
 *
 * `null` means either nothing is being drafted or what is being drafted is
 * something else, and a screen treats both the same way: its sheet is shut.
 */
export function useDraftValues<V>(key: string): V | null {
  const draft = useDraft();
  if (!draft || draft.key !== key || draft.minimised) return null;
  return draft.values as V;
}
